// Tiny hyperscript-style DOM helper. Keeps view code declarative without a
// framework. `el('div.card', { onclick }, ...children)`.

type Child = Node | string | number | null | undefined | false;

interface Props {
  [key: string]: unknown;
  onclick?: (e: MouseEvent) => void;
  html?: string;
  dataset?: Record<string, string>;
  style?: Partial<CSSStyleDeclaration> | string;
}

export function el<K extends keyof HTMLElementTagNameMap>(
  selector: K | string,
  props: Props = {},
  ...children: Child[]
): HTMLElement {
  const [tag, ...rest] = selector.split(/(?=[.#])/);
  const node = document.createElement(tag || 'div');
  for (const token of rest) {
    if (token.startsWith('.')) node.classList.add(token.slice(1));
    else if (token.startsWith('#')) node.id = token.slice(1);
  }
  for (const [k, v] of Object.entries(props)) {
    if (v == null) continue;
    if (k === 'onclick') node.addEventListener('click', v as EventListener);
    else if (k === 'html') node.innerHTML = v as string;
    else if (k === 'dataset') Object.assign(node.dataset, v);
    else if (k === 'style') {
      if (typeof v === 'string') node.setAttribute('style', v);
      else Object.assign(node.style, v);
    } else if (k in node) (node as unknown as Record<string, unknown>)[k] = v;
    else node.setAttribute(k, String(v));
  }
  for (const c of children.flat()) {
    if (c == null || c === false) continue;
    node.append(c instanceof Node ? c : document.createTextNode(String(c)));
  }
  return node;
}

/** Attach a class-prefixed <style> once per component id. */
export function ensureStyle(id: string, css: string): void {
  if (document.getElementById(id)) return;
  const style = document.createElement('style');
  style.id = id;
  style.textContent = css;
  document.head.append(style);
}
